// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Grid,
  Column,
  Button,
  InlineNotification,
  Tag,
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Tile,
  ProgressIndicator,
  ProgressStep,
  Modal,
  TextInput,
  TextArea,
  Select,
  SelectItem,
  Dropdown,
} from '@carbon/react';
import {
  Collaborate,
  Document,
  Analytics,
  UserMultiple,
  CheckmarkFilled,
  InProgress,
  Warning,
  Add,
  Edit,
  View,
  Download,
  Share,
} from '@carbon/icons-react';
import { useAuth } from '../../hooks/useAuth';
import { useCollaboration } from '../../hooks/useCollaboration';
import CollaborationSession from '../../components/collaboration/CollaborationSession';
import ParticipantsList from '../../components/collaboration/ParticipantsList';
import CollaborativeEditor from '../../components/collaboration/CollaborativeEditor';

interface TeamProject {
  id: string;
  name: string;
  description: string;
  status: 'planning' | 'in_progress' | 'review' | 'completed';
  progress: number;
  teamMembers: string[];
  deadline: Date;
  department: string;
}

interface TeamMember {
  id: string;
  name: string;
  role: string;
  department: string;
  status: 'available' | 'busy' | 'away';
  currentProject?: string;
  permissions: string[];
}

interface ProjectDocument {
  id: string;
  name: string;
  type: 'document' | 'spreadsheet' | 'presentation' | 'report';
  projectId: string;
  version: number;
  lastModifiedBy: string;
  lastModified: Date;
  collaborators: string[];
}

const TeamWorkspace: React.FC = () => {
  const { projectId } = useParams<{ projectId?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [projects, setProjects] = useState<TeamProject[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [documents, setDocuments] = useState<ProjectDocument[]>([]);
  const [selectedProject, setSelectedProject] = useState<TeamProject | null>(null);
  const [newProjectModalOpen, setNewProjectModalOpen] = useState(false);
  const [newProject, setNewProject] = useState<Partial<TeamProject>>({});
  const [activeView, setActiveView] = useState<'projects' | 'documents' | 'team'>('projects');

  const {
    participants,
    isConnected,
    isReconnecting,
    connectionError,
    edits,
    conflicts,
    annotations,
    capabilities,
    sendEdit,
    createAnnotation,
    resolveConflict,
  } = useCollaboration({
    sessionId: projectId ? `project-${projectId}` : `team-${user?.organization_id}`,
    autoConnect: true,
    onConnect: () => console.log('Connected to team workspace'),
    onError: error => console.error('Team workspace error:', error),
  });

  useEffect(() => {
    const fetchTeamData = async () => {
      try {
        // Fetch team projects
        const projectsResponse = await fetch('/api/v1/enterprise/projects', {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        });
        if (projectsResponse.ok) {
          const projectsData = await projectsResponse.json();
          setProjects(projectsData);
        }

        // Fetch team members
        const membersResponse = await fetch('/api/v1/enterprise/team-members', {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        });
        if (membersResponse.ok) {
          const membersData = await membersResponse.json();
          setTeamMembers(membersData);
        }

        // Fetch project documents
        if (projectId) {
          const docsResponse = await fetch(`/api/v1/enterprise/projects/${projectId}/documents`, {
            headers: {
              Authorization: `Bearer ${user?.token}`,
            },
          });
          if (docsResponse.ok) {
            const docsData = await docsResponse.json();
            setDocuments(docsData);
          }

          const project = projectsData?.find((p: TeamProject) => p.id === projectId);
          setSelectedProject(project || null);
        }
      } catch (error) {
        console.error('Error fetching team data:', error);
      }
    };

    fetchTeamData();
  }, [user, projectId]);

  const createProject = async () => {
    try {
      const response = await fetch('/api/v1/enterprise/projects', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user?.token}`,
        },
        body: JSON.stringify(newProject),
      });

      if (response.ok) {
        const project = await response.json();
        setProjects([...projects, project]);
        setNewProjectModalOpen(false);
        setNewProject({});
        navigate(`/enterprise/team-workspace/${project.id}`);
      }
    } catch (error) {
      console.error('Error creating project:', error);
    }
  };

  const getStatusColor = (status: TeamProject['status']) => {
    switch (status) {
      case 'planning':
        return 'cool-gray';
      case 'in_progress':
        return 'blue';
      case 'review':
        return 'purple';
      case 'completed':
        return 'green';
      default:
        return 'cool-gray';
    }
  };

  const getDocumentIcon = (type: ProjectDocument['type']) => {
    switch (type) {
      case 'document':
        return <Document size={16} />;
      case 'spreadsheet':
        return <Analytics size={16} />;
      case 'presentation':
        return <View size={16} />;
      case 'report':
        return <Edit size={16} />;
      default:
        return null;
    }
  };

  return (
    <div className="team-workspace">
      <Grid fullWidth>
        <Column lg={16} md={8} sm={4}>
          <div className="workspace-header">
            <h1>Team Collaboration Workspace</h1>
            <div className="workspace-controls">
              <div className="view-selector">
                <Button
                  kind={activeView === 'projects' ? 'primary' : 'tertiary'}
                  size="sm"
                  onClick={() => setActiveView('projects')}
                >
                  Projects
                </Button>
                <Button
                  kind={activeView === 'documents' ? 'primary' : 'tertiary'}
                  size="sm"
                  onClick={() => setActiveView('documents')}
                >
                  Documents
                </Button>
                <Button
                  kind={activeView === 'team' ? 'primary' : 'tertiary'}
                  size="sm"
                  onClick={() => setActiveView('team')}
                >
                  Team
                </Button>
              </div>
              <div className="connection-status">
                {isConnected ? (
                  <Tag type="green">
                    <CheckmarkFilled size={16} />
                    Connected
                  </Tag>
                ) : isReconnecting ? (
                  <Tag type="cool-gray">
                    <InProgress size={16} />
                    Reconnecting...
                  </Tag>
                ) : (
                  <Tag type="red">
                    <Warning size={16} />
                    Disconnected
                  </Tag>
                )}
                <Tag type="blue">
                  <UserMultiple size={16} />
                  {participants.length} Active
                </Tag>
              </div>
            </div>
          </div>
        </Column>

        {connectionError && (
          <Column lg={16} md={8} sm={4}>
            <InlineNotification kind="error" title="Connection Error" subtitle={connectionError.message} lowContrast />
          </Column>
        )}

        {activeView === 'projects' && (
          <Column lg={16} md={8} sm={4}>
            <div className="projects-view">
              <div className="section-header">
                <h2>Active Projects</h2>
                <Button kind="primary" renderIcon={Add} onClick={() => setNewProjectModalOpen(true)}>
                  New Project
                </Button>
              </div>
              <div className="projects-grid">
                {projects.map(project => (
                  <Tile key={project.id} className="project-tile">
                    <div className="project-header">
                      <h3>{project.name}</h3>
                      <Tag type={getStatusColor(project.status)}>{project.status.replace('_', ' ')}</Tag>
                    </div>
                    <p className="project-description">{project.description}</p>
                    <div className="project-meta">
                      <div className="project-progress">
                        <span>Progress: {project.progress}%</span>
                        <ProgressIndicator
                          currentIndex={Math.floor((project.progress / 100) * 4)}
                          spaceEqually
                          className="project-progress-indicator"
                        >
                          <ProgressStep label="Start" />
                          <ProgressStep label="25%" />
                          <ProgressStep label="50%" />
                          <ProgressStep label="75%" />
                          <ProgressStep label="Complete" />
                        </ProgressIndicator>
                      </div>
                      <div className="project-deadline">
                        <span>Due: {new Date(project.deadline).toLocaleDateString()}</span>
                      </div>
                      <div className="project-team">
                        <UserMultiple size={16} />
                        <span>{project.teamMembers.length} members</span>
                      </div>
                    </div>
                    <div className="project-actions">
                      <Button
                        kind="ghost"
                        size="sm"
                        renderIcon={View}
                        onClick={() => navigate(`/enterprise/team-workspace/${project.id}`)}
                      >
                        Open
                      </Button>
                      <Button kind="ghost" size="sm" renderIcon={Collaborate}>
                        Collaborate
                      </Button>
                    </div>
                  </Tile>
                ))}
              </div>
            </div>
          </Column>
        )}

        {activeView === 'documents' && selectedProject && (
          <Column lg={16} md={8} sm={4}>
            <div className="documents-view">
              <Grid>
                <Column lg={10} md={5} sm={4}>
                  <CollaborationSession
                    sessionId={projectId ? `project-${projectId}` : `team-${user?.organization_id}`}
                    userId={user?.id || ''}
                    token={user?.token || ''}
                    mode="participant"
                    sessionTitle={selectedProject.name}
                  >
                    <div className="document-editor">
                      <h3>{selectedProject.name} - Collaborative Document</h3>
                      <CollaborativeEditor
                        onEdit={(content, position) => sendEdit(content, position)}
                        edits={edits}
                        conflicts={conflicts}
                        onResolveConflict={resolveConflict}
                      />
                      <div className="document-annotations">
                        {annotations.map((annotation, index) => (
                          <div key={index} className="annotation-item">
                            <Tag type={annotation.type === 'note' ? 'blue' : 'purple'}>{annotation.type}</Tag>
                            <span>{annotation.content}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CollaborationSession>
                </Column>
                <Column lg={6} md={3} sm={4}>
                  <Tile>
                    <h3>Project Documents</h3>
                    <TableContainer>
                      <Table size="sm">
                        <TableHead>
                          <TableRow>
                            <TableHeader>Name</TableHeader>
                            <TableHeader>Type</TableHeader>
                            <TableHeader>Version</TableHeader>
                            <TableHeader>Actions</TableHeader>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {documents.map(doc => (
                            <TableRow key={doc.id}>
                              <TableCell>{doc.name}</TableCell>
                              <TableCell>
                                {getDocumentIcon(doc.type)}
                                {doc.type}
                              </TableCell>
                              <TableCell>v{doc.version}</TableCell>
                              <TableCell>
                                <Button kind="ghost" size="sm" renderIcon={Edit}>
                                  Edit
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Tile>
                  <Tile className="team-collaborators-tile">
                    <h4>Active Collaborators</h4>
                    <ParticipantsList participants={participants} currentUserId={user?.id} />
                  </Tile>
                </Column>
              </Grid>
            </div>
          </Column>
        )}

        {activeView === 'team' && (
          <Column lg={16} md={8} sm={4}>
            <div className="team-view">
              <h2>Team Members</h2>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeader>Name</TableHeader>
                      <TableHeader>Role</TableHeader>
                      <TableHeader>Department</TableHeader>
                      <TableHeader>Status</TableHeader>
                      <TableHeader>Current Project</TableHeader>
                      <TableHeader>Actions</TableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {teamMembers.map(member => (
                      <TableRow key={member.id}>
                        <TableCell>{member.name}</TableCell>
                        <TableCell>{member.role}</TableCell>
                        <TableCell>{member.department}</TableCell>
                        <TableCell>
                          <Tag
                            type={member.status === 'available' ? 'green' : member.status === 'busy' ? 'red' : 'gray'}
                          >
                            {member.status}
                          </Tag>
                        </TableCell>
                        <TableCell>{member.currentProject || '-'}</TableCell>
                        <TableCell>
                          <Button kind="ghost" size="sm">
                            Message
                          </Button>
                          <Button kind="ghost" size="sm">
                            Assign
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          </Column>
        )}
      </Grid>

      <Modal
        open={newProjectModalOpen}
        onRequestClose={() => setNewProjectModalOpen(false)}
        modalHeading="Create New Project"
        primaryButtonText="Create"
        secondaryButtonText="Cancel"
        onRequestSubmit={createProject}
      >
        <TextInput
          id="project-name"
          labelText="Project Name"
          placeholder="Enter project name"
          value={newProject.name || ''}
          onChange={e => setNewProject({ ...newProject, name: e.target.value })}
        />
        <TextArea
          id="project-description"
          labelText="Description"
          placeholder="Enter project description"
          value={newProject.description || ''}
          onChange={e => setNewProject({ ...newProject, description: e.target.value })}
        />
        <Select
          id="project-department"
          labelText="Department"
          value={newProject.department || ''}
          onChange={e => setNewProject({ ...newProject, department: e.target.value })}
        >
          <SelectItem value="" text="Select department" />
          <SelectItem value="engineering" text="Engineering" />
          <SelectItem value="marketing" text="Marketing" />
          <SelectItem value="sales" text="Sales" />
          <SelectItem value="support" text="Support" />
          <SelectItem value="operations" text="Operations" />
        </Select>
      </Modal>
    </div>
  );
};

export default TeamWorkspace;
